import { inject, provide, ssrRef, useContext } from '@nuxtjs/composition-api';
import { localStore } from '~/modules/common/utils/localStorageUtils';

export const profileStoreKey = Symbol('profileStore');
/**
 * Profile Store
 */
export function installProfileContext() {
  const { $service, i18n } = useContext();

  /**
   * User Profile State
   */
  const state = ssrRef({
    user: {},
    userArticles: [],
    userArticlesPagination: null,
    userCommentsPagination: null,
    userFavorites: [],
    userFollowers: [],
    userFollowing: [],
    userComments: [],
    countryList: [],
    genderList: [
      {
        name: i18n.t('use-profile.choose'),
        code: 'unknown',
      },
      {
        name: i18n.t('use-profile.famele'),
        code: 'female',
      },
      {
        name: i18n.t('use-profile.male'),
        code: 'male',
      },
    ],
  });

  const getUser = async (username) => {
    const { data } = await $service.profile.getUser(username);
    state.value.user = data;
  };
  const setToggleUserBlock = (user) => {
    state.value.user.blockStatus = !state.value.user.blockStatus;
    if (state.value.user.blockStatus) {
      state.value.user.followStatus = false;
      state.value.user.stats.followers--;
      state.value.userFollowers = state.value.userFollowers.filter((follower) => follower._id !== user.value._id);
      state.value.userFollowing = state.value.userFollowing.filter((follower) => follower._id !== user.value._id);
    }
  };
  const setToggleUserFollow = (user) => {
    const status = !state.value.user.followStatus;
    const followers = state.value.user.stats.followers;
    state.value.user.followStatus = status;
    state.value.user.stats.followers = status ? followers + 1 : followers - 1;
    if (status) {
      state.value.userFollowers.unshift(user.value);
    } else {
      state.value.userFollowers = state.value.userFollowers.filter((follower) => follower._id !== user.value._id);
    }
  };
  const getUserArticles = async ({ userId, page }) => {
    const { data, pagination } = await $service.profile.getUserArticles({ userId, page });
    state.value.userArticlesPagination = pagination;
    state.value.userArticles = page === 1 ? data : state.value.userArticles.concat(data);
  };
  async function deleteArticle(articleId) {
    await $service.profile.deleteArticle(articleId);
    state.value.userArticles = state.value.userArticles.filter((item) => item._id !== articleId);
  }
  const getUserFavorites = async ({ userId, page }) => {
    const { data } = await $service.profile.getUserFavorites({ userId, page });
    state.value.userFavorites = page === 1 ? data : state.value.userFavorites.concat(data);
  };
  const getUserFollowers = async ({ userId, page, check }) => {
    const { data } = await $service.profile.getUserFollowers({ userId, page, check });
    state.value.userFollowers = page === 1 ? data : state.value.userFollowers.concat(data);
  };
  const getUserFollowing = async ({ userId, page, check }) => {
    const { data } = await $service.profile.getUserFollowing({ userId, page, check });
    state.value.userFollowing = page === 1 ? data : state.value.userFollowing.concat(data);
  };
  const getUserComments = async ({ userId, page }) => {
    const { data, pagination } = await $service.profile.getUserComments({ userId, page });
    state.value.userComments = page === 1 ? data : state.value.userComments.concat(data);
    state.value.userCommentsPagination = pagination;
  };
  const getCountryList = async () => {
    const countryListLocalStore = localStore.getItem('o-country-list');
    if (countryListLocalStore) {
      state.value.countryList = countryListLocalStore;
    } else {
      const { data } = await $service.profile.getCountryList();
      state.value.countryList = data;
      localStore.setItem('o-country-list', data);
    }
  };

  provide(profileStoreKey, {
    state,
    getUser,
    setToggleUserBlock,
    setToggleUserFollow,
    getUserArticles,
    deleteArticle,
    getUserFavorites,
    getUserFollowers,
    getUserFollowing,
    getUserComments,
    getCountryList,
  });
}

/**
 * @typedef {import("@nuxtjs/composition-api").Ref.<{
 *   user: object
 *   userArticles: array
 *   userFavorites: array
 *   userFollowers: array
 *   userFollowing: array
 *   userComments: array
 * }>} ProfileState
 */

/**
 * @typedef {Object} ProfileStore
 * @property {ProfileState} state
 * @property {function} getUser,
 * @property {function} setToggleUserBlock,
 * @property {function} setToggleUserFollow,
 * @property {function} getUserArticles,
 * @property {function} deleteArticle,
 * @property {function} getUserFavorites,
 * @property {function} getUserFollowers,
 * @property {function} getUserFollowing,
 * @property {function} getUserComments,
 * @property {function} getCountryList,
 */

/**
 * @returns {ProfileStore} Profile Store
 */
export function useProfile() {
  return inject(profileStoreKey);
}
